import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import noop from 'lodash/noop';
import { IDataChart, IPrepareDataChart } from '@/creditflowresearch/domain/vo/Bond/IDataChart';
import { IQueryParams } from '@/creditflowresearch/domain/vo/IQueryParams';
import { getContainer } from '@framework/di/getContainer';
import BondsRepository from '@/creditflowresearch/infrastructure/repository/BondsRepository';
import { BWICS, INVENTORIES } from '@/creditflowresearch/domain/BondType';
import { Periods } from '@/creditflowresearch/ui/common/constants';
import { getGroupList, prepareAndGroupData, setExtremesOfChart } from './helpers';
import { transformChartData } from './helpers/transformChartData';
import {
  ChartDataType,
  ChartsGrouping,
  ChartType,
  SectorGrouping,
} from '@/creditflowresearch/application/Bonds/charts/constants';
import { IChartDataAndActions } from '@/creditflowresearch/domain/vo/Bond/IChartDataAndActions';
import { IBondsFilterState } from '@/creditflowresearch/domain/vo/Bond/BondsFiltersContextData';
import { useBondsAssetClassesContext } from '@/creditflowresearch/application/Bonds/BondsAssetClassesContext';
import { isScreenXs } from '@creditflowresearch/uilib/utils/screen';
import { useBondsFiltersContext } from '@/creditflowresearch/application/Bonds/BondsFiltersContext';
import useQuery from '@/creditflowresearch/infrastructure/query';

const EXTREMES_LIMITS = {
  [Periods.MONTH]: 6,
  [Periods.QUARTER]: 2,
  [Periods.YEAR]: 2,
};

const EMPTY_CHART_DATA: IDataChart[] = [];

interface IBondChartsPayload {
  search: string | string[] | undefined,
  groupBy: SectorGrouping | undefined,
  rangeBy: Periods | undefined,
  filter: IBondsFilterState & { includedAssetClassId: string | undefined },
}

interface IProps {
  request: ((payload: IBondChartsPayload) => Promise<IDataChart[]>) & { rpcMethod?: string },
  isSector: boolean,
  key: string,
  isShowCharts: boolean,
}

export const useChart = ({ request, isSector, key, isShowCharts }: IProps): IChartDataAndActions => {
  const { query: { bondType, q: search } }: { query: IQueryParams } = useRouter();

  const [prepareData, setPrepareData] = useState<IPrepareDataChart[]>([]);
  const [isEmptyData, setIsEmptyData] = useState<boolean>(false);

  const [period, setPeriod] = useState<Periods>(Periods.MONTH);
  const [grouping, setGrouping] = useState<ChartsGrouping>(ChartsGrouping.VOLUME);
  const [chartType, setChartType] = useState<ChartType>(ChartType.GROUPED);
  const [sectorGrouping, setSectorGrouping] = useState<SectorGrouping>(SectorGrouping.SECTOR);

  const { filtersPayload } = useBondsFiltersContext();
  const { currentAssetClass } = useBondsAssetClassesContext();

  const isSetExtremes = useRef(true);

  const onChangePeriod = (period: Periods) => (setPeriod(period));
  const onChangeChartType = (chartType: ChartType) => (setChartType(chartType));
  const onChangeGrouping = (grouping: ChartsGrouping) => (setGrouping(grouping));
  const onChangeSector = isSector ? ((sectorGrouping: SectorGrouping) => (setSectorGrouping(sectorGrouping))) : noop;

  const bondChartsPayload = useMemo((): IBondChartsPayload => ({
    search,
    groupBy: isSector ? sectorGrouping : undefined,
    rangeBy: key === INVENTORIES ? period : undefined,
    filter: { ...filtersPayload, includedAssetClassId: currentAssetClass?.id },
  }), [search, isSector, sectorGrouping, key, period, filtersPayload, currentAssetClass]);

  const fetchChartData = useCallback(
    async (payload: IBondChartsPayload) => transformChartData(await request(payload), {
      isSector,
      groupBy: payload.groupBy,
    }),
    [request, isSector],
  );

  const shouldGetChart = isShowCharts && key === bondType;
  // INVENTORIES keeps refreshing on period change regardless of the active tab;
  // BWICS only fetches while its own tab is active.
  const enabled = key === INVENTORIES ? isShowCharts : shouldGetChart;

  // Explicit key: the wrapper hides request.rpcMethod from useQuery's default.
  const { data, isFetching } = useQuery<IBondChartsPayload, IDataChart[]>(
    fetchChartData,
    bondChartsPayload,
    { enabled, queryKey: request.rpcMethod },
  );
  const clearData = data ?? EMPTY_CHART_DATA;

  const handlePrepareData = () => {
    const prepareData = prepareAndGroupData(clearData, period, grouping);

    setIsEmptyData(prepareData.length === 0);

    setPrepareData(getGroupList(prepareData));
  };

  const events = {
    load() {
      setExtremesOfChart(this, EXTREMES_LIMITS[period]);
    },
    redraw() {
      if (!isSetExtremes.current) {
        isSetExtremes.current = true;
        setExtremesOfChart(this, EXTREMES_LIMITS[period]);
      }
    },
  };

  useEffect(() => {
    // period/isShowCharts also regroup already-fetched BWICS data locally,
    // since a period toggle there doesn't change the RPC payload.
    handlePrepareData();
    isSetExtremes.current = false;
  }, [clearData, grouping, period, isShowCharts]);

  useEffect(() => {
    isSetExtremes.current = false;
  }, [period, bondType]);

  return {
    data: prepareData,
    isEmptyData,
    isFetching,
    period,
    chartType,
    grouping,
    sectorGrouping: isSector ? sectorGrouping : undefined,
    isSector,
    events,
    actions: {
      onChangePeriod,
      onChangeChartType,
      onChangeGrouping,
      onChangeSector,
    },
  };
};

interface IBondChartsValue {
  dealer: IChartDataAndActions,
  sector: IChartDataAndActions,
  isShowCharts: boolean,
  setIsShowCharts: Dispatch<SetStateAction<boolean>>,
}

export const useBondsCharts = (): IBondChartsValue => {
  const { query }: { query: IQueryParams } = useRouter();
  const [currentBondType] = Array.isArray(query.bondType) ? query.bondType : [query.bondType];

  const container = getContainer();
  const bondsRepository = container.get(BondsRepository);

  const [isShowCharts, setIsShowCharts] = useState(true);

  useEffect(() => {
    setIsShowCharts(!isScreenXs());
  }, []);

  const charts = {
    [BWICS]: {
      [ChartDataType.DEALER_CHART]: useChart({
        request: bondsRepository.getBwicDealerChartData, isSector: false, key: BWICS, isShowCharts,
      }),
      [ChartDataType.SECTOR_CHART]: useChart({
        request: bondsRepository.getBwicSectorChartData, isSector: true, key: BWICS, isShowCharts,
      }),
    },
    [INVENTORIES]: {
      [ChartDataType.DEALER_CHART]: useChart({
        request: bondsRepository.getInventoryDealerChartData, isSector: false, key: INVENTORIES, isShowCharts,
      }),
      [ChartDataType.SECTOR_CHART]: useChart({
        request: bondsRepository.getInventorySectorChartData, isSector: true, key: INVENTORIES, isShowCharts,
      }),
    },
  };

  return {
    dealer: charts[currentBondType][ChartDataType.DEALER_CHART],
    sector: charts[currentBondType][ChartDataType.SECTOR_CHART],
    isShowCharts,
    setIsShowCharts,
  };
};
